import { heroById } from '@/data/heroes';
import { type CharacterState } from '@/game/character';
import { type Npc } from '@/game/npc';
import { type WorldScene } from '@/world/zone';

const MAX_CSS_WIDTH = 240;
const MAX_CSS_HEIGHT = 120;

export class Minimap {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private cssWidth = MAX_CSS_WIDTH;
  private cssHeight = MAX_CSS_HEIGHT;
  private visible = false;
  private lastDrawMs = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('minimap: 2D context unavailable');
    this.ctx = ctx;
  }

  /** Lay out the canvas to fit the world's aspect ratio inside the
   *  configured max width × height. */
  fitToWorld(world: WorldScene): void {
    const b = world.bounds;
    const worldW = b.maxX - b.minX;
    const worldH = b.maxZ - b.minZ;
    const widthByH = MAX_CSS_HEIGHT * (worldW / worldH);
    const heightByW = MAX_CSS_WIDTH * (worldH / worldW);
    if (widthByH <= MAX_CSS_WIDTH) {
      this.cssWidth = Math.round(widthByH);
      this.cssHeight = MAX_CSS_HEIGHT;
    } else {
      this.cssWidth = MAX_CSS_WIDTH;
      this.cssHeight = Math.round(heightByW);
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(this.cssWidth * dpr);
    this.canvas.height = Math.round(this.cssHeight * dpr);
    this.canvas.style.width = `${this.cssWidth}px`;
    this.canvas.style.height = `${this.cssHeight}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return;
    this.visible = visible;
    this.canvas.classList.toggle('visible', visible);
  }

  render(player: CharacterState, npcs: ReadonlyArray<Npc>, world: WorldScene, nowMs: number): void {
    if (!this.visible) return;
    if (nowMs - this.lastDrawMs < 80) return;
    this.lastDrawMs = nowMs;

    const ctx = this.ctx;
    const W = this.cssWidth;
    const H = this.cssHeight;
    const b = world.bounds;
    const worldW = b.maxX - b.minX;
    const worldH = b.maxZ - b.minZ;
    const wx = (x: number): number => ((x - b.minX) / worldW) * W;
    const wz = (z: number): number => ((z - b.minZ) / worldH) * H;

    ctx.fillStyle = '#2c3a26';
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, W - 1, H - 1);

    // Dirt paths — trace each path's polyline.
    ctx.strokeStyle = '#7a5a3a';
    ctx.lineWidth = Math.max(2, (H / worldH) * 3.2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const path of world.paths) {
      ctx.beginPath();
      for (let i = 0; i < path.length; i++) {
        const p = path[i]!;
        if (i === 0) ctx.moveTo(wx(p.x), wz(p.z));
        else ctx.lineTo(wx(p.x), wz(p.z));
      }
      ctx.stroke();
    }

    // Town markers (small fire-coloured discs).
    ctx.fillStyle = '#ff8b3a';
    ctx.strokeStyle = '#2a1a10';
    ctx.lineWidth = 1;
    for (const town of world.towns) {
      const tx = wx(town.x);
      const ty = wz(town.z);
      ctx.beginPath();
      ctx.arc(tx, ty, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    // NPCs
    for (const npc of npcs) {
      if (npc.recruited || npc.defeated) continue;
      const t = heroById(npc.templateId);
      ctx.fillStyle = `rgb(${Math.floor(t.visual.tintR * 255)}, ${Math.floor(t.visual.tintG * 255)}, ${Math.floor(t.visual.tintB * 255)})`;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(wx(npc.x), wz(npc.z), 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    // Player + facing tick
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(wx(player.x), wz(player.z), 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    const fx = wx(player.x) + Math.sin(player.yaw) * 7;
    const fy = wz(player.z) - Math.cos(player.yaw) * 7;
    ctx.beginPath();
    ctx.moveTo(wx(player.x), wz(player.z));
    ctx.lineTo(fx, fy);
    ctx.strokeStyle = '#ffd45a';
    ctx.lineWidth = 1.6;
    ctx.stroke();
  }
}
